/**
 * Half-day divider/prefix from Codes & devices — used for both import and export files.
 * Internal attendance storage stays slash-separated (e.g. PR/LOP) for the UI.
 */

export function normalizeHalfDayIoFormat(divider, prefix) {
  return {
    divider:
      divider != null && String(divider).trim() !== ""
        ? String(divider).trim()
        : "",
    prefix:
      prefix != null && String(prefix).trim() !== ""
        ? String(prefix).trim()
        : "",
  };
}

export function isHalfDayIoFormatConfigured(divider, prefix) {
  const { divider: d, prefix: p } = normalizeHalfDayIoFormat(divider, prefix);
  return !!(d || p);
}

/** Write half-day into an import/export cell. */
export function formatHalfDayIoCell(first, second, divider, prefix) {
  const a = String(first || "").trim();
  const b = String(second || "").trim();
  if (!a && !b) return "";
  if (!a || !b || a.toUpperCase() === b.toUpperCase()) return a || b;
  const { divider: div, prefix: pref } = normalizeHalfDayIoFormat(
    divider,
    prefix
  );
  if (pref) return `${pref}${a}${div}${pref}${b}`;
  if (div) return `${a}${div}${b}`;
  // No divider/prefix configured → plain concatenation (e.g. PRLOP)
  return `${a}${b}`;
}

function stripHalfDayPrefix(seg, pref, requirePref) {
  let s = String(seg || "").trim();
  if (!pref) return s;
  if (s.toLowerCase().startsWith(pref.toLowerCase())) {
    return s.slice(pref.length).trim();
  }
  return requirePref ? null : s;
}

/**
 * Strict parse for import files when divider/prefix are configured.
 * Returns null if the cell does not match the configured format.
 */
export function parseHalfDayIoStrict(rawStatus, divider, prefix) {
  const raw = String(rawStatus || "").trim();
  if (!raw) return null;
  const { divider: div, prefix: pref } = normalizeHalfDayIoFormat(
    divider,
    prefix
  );
  if (!(div || pref)) return null;
  if (!div || !raw.includes(div)) return null;
  const parts = raw
    .split(div)
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  if (parts.length !== 2) return null;
  const a = stripHalfDayPrefix(parts[0], pref, !!pref);
  const b = stripHalfDayPrefix(parts[1], pref, !!pref);
  if (a == null || b == null || !a || !b) return null;
  return [a, b];
}

/**
 * Lenient parse for reading DB/UI values before reformatting for export,
 * and for legacy import when no divider/prefix is configured.
 */
export function parseHalfDayIoLenient(rawStatus, divider, prefix, splitCombined) {
  const raw = String(rawStatus || "").trim();
  if (!raw) return null;
  const { divider: div, prefix: pref } = normalizeHalfDayIoFormat(
    divider,
    prefix
  );

  if (div && raw.includes(div)) {
    const parts = raw
      .split(div)
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    if (parts.length === 2) {
      const a = stripHalfDayPrefix(parts[0], pref, false);
      const b = stripHalfDayPrefix(parts[1], pref, false);
      if (a && b) return [a, b];
    }
  }

  if (raw.includes("+")) {
    const parts = raw
      .split("+")
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    if (parts.length === 2) {
      const a = stripHalfDayPrefix(parts[0], pref, false);
      const b = stripHalfDayPrefix(parts[1], pref, false);
      if (a && b) return [a, b];
    }
  }

  const slashCount = (raw.match(/\//g) || []).length;
  if (slashCount === 1) {
    const [a, b] = raw.split("/");
    if (a?.trim() && b?.trim()) return [a.trim(), b.trim()];
  }

  if (typeof splitCombined === "function") {
    const combined = splitCombined(raw);
    if (combined && combined.length === 2) return combined;
  }
  return null;
}
